import { type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { SectionLabel } from '../layout/Typography';
import { StatCard } from './StatCard';

/**
 * Left-border accent tokens a {@link StatGridItem} can request. Each maps to
 * a `status-*` design token so the palette stays theme-aware in light/dark.
 */
export type StatAccent =
  | 'violet'
  | 'purple'
  | 'teal'
  | 'cyan'
  | 'indigo'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'neutral';

const ACCENT_CLASS: Record<StatAccent, string> = {
  violet: 'border-l-2 border-l-status-accent-violet/60',
  purple: 'border-l-2 border-l-status-accent-purple/60',
  teal: 'border-l-2 border-l-status-accent-teal/60',
  cyan: 'border-l-2 border-l-status-accent-cyan/60',
  indigo: 'border-l-2 border-l-status-accent-indigo/60',
  info: 'border-l-2 border-l-status-info/60',
  success: 'border-l-2 border-l-status-success/60',
  warning: 'border-l-2 border-l-status-warning/60',
  error: 'border-l-2 border-l-status-error/60',
  neutral: 'border-l-2 border-l-status-neutral/60',
};

export interface StatGridItem {
  /** Short uppercase label rendered above the value. */
  label: string;
  /** The headline figure. Accepts nodes so chips/icons can be embedded. */
  value: ReactNode;
  /** Optional sub-line under the value (e.g. "1.4k records"). */
  description?: ReactNode;
  /** Optional left-border accent color. */
  accent?: StatAccent;
  /** Override the value type scale (e.g. `text-base` for a chip value). */
  valueClassName?: string;
  /** Makes the card interactive when provided. */
  onClick?: () => void;
  /** Reflects the pressed/selected state on an interactive card. */
  selected?: boolean;
}

/**
 * Column ramps keyed by the number of stats. These use **container query**
 * variants (`@md`, `@2xl`, ...), not viewport breakpoints, so the grid reacts
 * to its own rendered width — correct inside narrow panes (plugin previews,
 * split layouts) and on real phones alike. Always two-up at the floor
 * (one-up for a single stat), widening to the full count only once the
 * container is wide enough that `text-3xl` figures aren't crammed.
 */
const COLUMNS_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 @lg:grid-cols-3',
  4: 'grid-cols-2 @2xl:grid-cols-4',
  5: 'grid-cols-2 @md:grid-cols-3 @4xl:grid-cols-5',
  6: 'grid-cols-2 @md:grid-cols-3 @4xl:grid-cols-6',
};

const FALLBACK_COLUMNS = 'grid-cols-2 @md:grid-cols-3 @4xl:grid-cols-5';

interface StatGridProps {
  /** The stats to render, left-to-right. */
  items: StatGridItem[];
  /**
   * Optional section heading. When set, the grid is wrapped in a `<section>`
   * with a {@link SectionLabel} so callers don't re-implement the chrome.
   */
  label?: string;
  className?: string;
}

/**
 * Responsive grid of {@link StatCard}s with left-border accents. Encapsulates
 * the "KPI row" pattern (label + accent stat cards + adaptive columns) so
 * pages declare data, not layout. The wrapper is a `@container`, so the column
 * count adapts to the grid's own width — collapsing to two-up in narrow panes
 * and on phones regardless of the surrounding viewport.
 */
export function StatGrid({ items, label, className }: StatGridProps) {
  const cols = COLUMNS_CLASS[Math.min(Math.max(items.length, 1), 6)] ?? FALLBACK_COLUMNS;

  const grid = (
    <div data-testid="stat-grid" className={cn('grid gap-3', cols)}>
      {items.map((item) => (
        <StatCard
          key={item.label}
          label={item.label}
          value={item.value}
          description={item.description}
          valueClassName={item.valueClassName}
          onClick={item.onClick}
          selected={item.selected}
          className={item.accent ? ACCENT_CLASS[item.accent] : undefined}
        />
      ))}
    </div>
  );

  if (label) {
    return (
      <section className={cn('@container', className)}>
        <SectionLabel className="mb-3">{label}</SectionLabel>
        {grid}
      </section>
    );
  }

  return <div className={cn('@container', className)}>{grid}</div>;
}
